/**
 * Translations for satoshi converter pages.
 * Each currency maps to a local language code + translations.
 * English-speaking currencies (usd, aud, gbp, etc.) have no local language — they stay English.
 */

export interface TranslationStrings {
  convertSatoshisTo: string; // "Convert Satoshis to {code}"
  instantlyConvert: string; // subtitle
  satoshisLabel: string; // "Satoshis (sats)"
  enterSatoshis: string; // placeholder
  enterAmountIn: string; // "Enter amount in {code}"
  shareLinkLabel: string;
  copyLink: string;
  linkCopied: string;
  whatIsASatoshi: string;
  whatIsASatoshiBody: string;
  howSatoshisWork: string;
  fixedSupplyLabel: string;
  fixedSupplyText: string;
  hundredMillionLabel: string;
  hundredMillionText: string;
  whySatoshisMatterLabel: string;
  whySatoshisMatterText: string;
  lightningNetworkLabel: string;
  lightningNetworkText: string;
  convertingSatoshisTo: string; // "Converting Satoshis to {name} ({code})"
  conversionExplanation: string;
  calculatorExplanation: string;
  quickReference: string;
  convertToOtherCurrencies: string;
}

const en: TranslationStrings = {
  convertSatoshisTo: "Convert Satoshis to",
  instantlyConvert:
    "Instantly convert Bitcoin satoshis (sats) to {name} and back",
  satoshisLabel: "Satoshis (sats)",
  enterSatoshis: "Enter amount in satoshis",
  enterAmountIn: "Enter amount in {code}",
  shareLinkLabel: "Share this conversion:",
  copyLink: "Copy Link",
  linkCopied: "Link Copied!",
  whatIsASatoshi: "What Is a Satoshi?",
  whatIsASatoshiBody:
    'A satoshi (often abbreviated as "sat" or "sats") is the smallest unit of Bitcoin. It is named after Satoshi Nakamoto, the pseudonymous creator of Bitcoin, who published the Bitcoin whitepaper on October 31, 2008. The term "satoshi" was proposed by the early Bitcoin community as a convenient way to refer to tiny fractions of a Bitcoin.',
  howSatoshisWork: "How Satoshis Work in Bitcoin",
  fixedSupplyLabel: "Fixed Supply:",
  fixedSupplyText:
    "There will only ever be 21 million Bitcoin (21,000,000 BTC). This hard cap is enforced by the Bitcoin protocol and cannot be changed.",
  hundredMillionLabel: "100 Million Sats per Bitcoin:",
  hundredMillionText:
    "Each Bitcoin is divisible into 100,000,000 (one hundred million) satoshis. This means the total supply of satoshis is 2,100,000,000,000,000 (2.1 quadrillion).",
  whySatoshisMatterLabel: "Why Satoshis Matter:",
  whySatoshisMatterText:
    'As the price of Bitcoin rises, people increasingly transact in satoshis rather than whole Bitcoins. It\'s much more intuitive to say "500 sats" than "0.000005 BTC."',
  lightningNetworkLabel: "Lightning Network:",
  lightningNetworkText:
    "On the Lightning Network, payments are denominated in satoshis (or even millisatoshis — thousandths of a satoshi). This makes micropayments as small as a fraction of a penny possible.",
  convertingSatoshisTo: "Converting Satoshis to {name} ({code})",
  conversionExplanation:
    "To convert satoshis to {name}, divide the number of satoshis by 100,000,000 to get the BTC amount, then multiply by the current BTC/{code} exchange rate. For example:",
  calculatorExplanation:
    "The calculator above does this math instantly for any amount — just type a value in either the satoshis field or the {code} field and the conversion happens in real time. You'll also see the equivalent Bitcoin amount displayed.",
  quickReference: "Quick Reference",
  convertToOtherCurrencies: "Convert Satoshis to Other Currencies",
};

const es: TranslationStrings = {
  convertSatoshisTo: "Convertir Satoshis a",
  instantlyConvert:
    "Convierte instantáneamente satoshis de Bitcoin (sats) a {name} y viceversa",
  satoshisLabel: "Satoshis (sats)",
  enterSatoshis: "Ingresa cantidad en satoshis",
  enterAmountIn: "Ingresa cantidad en {code}",
  shareLinkLabel: "Compartir esta conversión:",
  copyLink: "Copiar enlace",
  linkCopied: "¡Enlace copiado!",
  whatIsASatoshi: "¿Qué es un Satoshi?",
  whatIsASatoshiBody:
    'Un satoshi (abreviado como "sat" o "sats") es la unidad más pequeña de Bitcoin. Lleva el nombre de Satoshi Nakamoto, el creador pseudónimo de Bitcoin, quien publicó el whitepaper de Bitcoin el 31 de octubre de 2008. El término "satoshi" fue propuesto por la comunidad temprana de Bitcoin como una forma conveniente de referirse a fracciones diminutas de un Bitcoin.',
  howSatoshisWork: "Cómo funcionan los Satoshis en Bitcoin",
  fixedSupplyLabel: "Suministro fijo:",
  fixedSupplyText:
    "Solo existirán 21 millones de Bitcoin (21,000,000 BTC). Este límite máximo es impuesto por el protocolo de Bitcoin y no puede ser modificado.",
  hundredMillionLabel: "100 millones de Sats por Bitcoin:",
  hundredMillionText:
    "Cada Bitcoin es divisible en 100,000,000 (cien millones) de satoshis. Esto significa que el suministro total de satoshis es de 2,100,000,000,000,000 (2.1 cuatrillones).",
  whySatoshisMatterLabel: "¿Por qué importan los Satoshis?",
  whySatoshisMatterText:
    'A medida que el precio de Bitcoin sube, las personas cada vez más realizan transacciones en satoshis en lugar de Bitcoins enteros. Es mucho más intuitivo decir "500 sats" que "0.000005 BTC".',
  lightningNetworkLabel: "Red Lightning:",
  lightningNetworkText:
    "En la Red Lightning, los pagos se denominan en satoshis (o incluso millisatoshis — milésimas de un satoshi). Esto hace posible micropagos tan pequeños como una fracción de centavo.",
  convertingSatoshisTo: "Convertir Satoshis a {name} ({code})",
  conversionExplanation:
    "Para convertir satoshis a {name}, divide la cantidad de satoshis por 100,000,000 para obtener la cantidad en BTC, luego multiplica por el tipo de cambio actual BTC/{code}. Por ejemplo:",
  calculatorExplanation:
    "La calculadora de arriba hace esta operación instantáneamente para cualquier cantidad — solo escribe un valor en el campo de satoshis o en el campo de {code} y la conversión ocurre en tiempo real. También verás la cantidad equivalente en Bitcoin.",
  quickReference: "Referencia rápida",
  convertToOtherCurrencies: "Convertir Satoshis a otras monedas",
};

const pt: TranslationStrings = {
  convertSatoshisTo: "Converter Satoshis para",
  instantlyConvert:
    "Converta instantaneamente satoshis de Bitcoin (sats) para {name} e vice-versa",
  satoshisLabel: "Satoshis (sats)",
  enterSatoshis: "Digite o valor em satoshis",
  enterAmountIn: "Digite o valor em {code}",
  shareLinkLabel: "Compartilhar esta conversão:",
  copyLink: "Copiar link",
  linkCopied: "Link copiado!",
  whatIsASatoshi: "O que é um Satoshi?",
  whatIsASatoshiBody:
    'Um satoshi (frequentemente abreviado como "sat" ou "sats") é a menor unidade do Bitcoin. É nomeado em homenagem a Satoshi Nakamoto, o criador pseudônimo do Bitcoin, que publicou o whitepaper do Bitcoin em 31 de outubro de 2008. O termo "satoshi" foi proposto pela comunidade inicial do Bitcoin como uma forma conveniente de se referir a frações minúsculas de um Bitcoin.',
  howSatoshisWork: "Como os Satoshis funcionam no Bitcoin",
  fixedSupplyLabel: "Oferta fixa:",
  fixedSupplyText:
    "Existirão apenas 21 milhões de Bitcoin (21.000.000 BTC). Esse limite máximo é imposto pelo protocolo Bitcoin e não pode ser alterado.",
  hundredMillionLabel: "100 milhões de Sats por Bitcoin:",
  hundredMillionText:
    "Cada Bitcoin é divisível em 100.000.000 (cem milhões) de satoshis. Isso significa que a oferta total de satoshis é de 2.100.000.000.000.000 (2,1 quatrilhões).",
  whySatoshisMatterLabel: "Por que os Satoshis importam:",
  whySatoshisMatterText:
    'À medida que o preço do Bitcoin sobe, as pessoas cada vez mais transacionam em satoshis em vez de Bitcoins inteiros. É muito mais intuitivo dizer "500 sats" do que "0,000005 BTC".',
  lightningNetworkLabel: "Rede Lightning:",
  lightningNetworkText:
    "Na Rede Lightning, os pagamentos são denominados em satoshis (ou até millisatoshis — milésimos de um satoshi). Isso torna possíveis micropagamentos tão pequenos quanto uma fração de centavo.",
  convertingSatoshisTo: "Converter Satoshis para {name} ({code})",
  conversionExplanation:
    "Para converter satoshis para {name}, divida o número de satoshis por 100.000.000 para obter a quantidade em BTC, depois multiplique pela taxa de câmbio atual BTC/{code}. Por exemplo:",
  calculatorExplanation:
    "A calculadora acima faz essa conta instantaneamente para qualquer valor — basta digitar um valor no campo de satoshis ou no campo de {code} e a conversão acontece em tempo real. Você também verá a quantidade equivalente em Bitcoin.",
  quickReference: "Referência rápida",
  convertToOtherCurrencies: "Converter Satoshis para outras moedas",
};

const ja: TranslationStrings = {
  convertSatoshisTo: "サトシを変換",
  instantlyConvert: "ビットコインのサトシ（sats）と{name}を即座に相互変換",
  satoshisLabel: "サトシ（sats）",
  enterSatoshis: "サトシの金額を入力",
  enterAmountIn: "{code}の金額を入力",
  shareLinkLabel: "この変換を共有：",
  copyLink: "リンクをコピー",
  linkCopied: "コピーしました！",
  whatIsASatoshi: "サトシとは？",
  whatIsASatoshiBody:
    "サトシ（「sat」や「sats」と略されます）は、ビットコインの最小単位です。2008年10月31日にビットコインのホワイトペーパーを発表した匿名の創設者、サトシ・ナカモトにちなんで名付けられました。「サトシ」という用語は、ビットコインの初期コミュニティによって、ビットコインの微小な端数を指す便利な方法として提案されました。",
  howSatoshisWork: "ビットコインにおけるサトシの仕組み",
  fixedSupplyLabel: "固定供給量：",
  fixedSupplyText:
    "ビットコインは2,100万枚（21,000,000 BTC）しか存在しません。この上限はビットコインのプロトコルによって強制され、変更することはできません。",
  hundredMillionLabel: "1ビットコイン = 1億サトシ：",
  hundredMillionText:
    "各ビットコインは100,000,000（1億）サトシに分割できます。つまり、サトシの総供給量は2,100,000,000,000,000（2.1京）です。",
  whySatoshisMatterLabel: "サトシが重要な理由：",
  whySatoshisMatterText:
    "ビットコインの価格が上昇するにつれ、人々はビットコイン全体ではなくサトシで取引することが増えています。「0.000005 BTC」よりも「500 sats」と言う方がはるかに直感的です。",
  lightningNetworkLabel: "ライトニングネットワーク：",
  lightningNetworkText:
    "ライトニングネットワークでは、支払いはサトシ（またはミリサトシ — サトシの千分の一）で表示されます。これにより、1円未満のマイクロペイメントが可能になります。",
  convertingSatoshisTo: "サトシを{name}（{code}）に変換",
  conversionExplanation:
    "サトシを{name}に変換するには、サトシの数を100,000,000で割ってBTC金額を求め、現在のBTC/{code}為替レートを掛けます。例えば：",
  calculatorExplanation:
    "上の計算機は任意の金額に対してこの計算を即座に行います。サトシフィールドまたは{code}フィールドに値を入力するだけで、リアルタイムで変換されます。同等のビットコイン金額も表示されます。",
  quickReference: "クイックリファレンス",
  convertToOtherCurrencies: "他の通貨にサトシを変換",
};

const zh: TranslationStrings = {
  convertSatoshisTo: "将聪转换为",
  instantlyConvert: "即时将比特币聪（sats）与{name}互相转换",
  satoshisLabel: "聪（sats）",
  enterSatoshis: "输入聪的数量",
  enterAmountIn: "输入{code}金额",
  shareLinkLabel: "分享此转换：",
  copyLink: "复制链接",
  linkCopied: "已复制！",
  whatIsASatoshi: "什么是聪？",
  whatIsASatoshiBody:
    '聪（通常缩写为"sat"或"sats"）是比特币的最小单位。它以比特币的匿名创造者中本聪（Satoshi Nakamoto）命名，他于2008年10月31日发布了比特币白皮书。"聪"这个术语由早期比特币社区提出，作为一种方便引用比特币微小部分的方式。',
  howSatoshisWork: "聪在比特币中的工作原理",
  fixedSupplyLabel: "固定供应量：",
  fixedSupplyText:
    "比特币总量只有2100万枚（21,000,000 BTC）。这一硬上限由比特币协议强制执行，不可更改。",
  hundredMillionLabel: "每个比特币 = 1亿聪：",
  hundredMillionText:
    "每个比特币可分为100,000,000（一亿）聪。这意味着聪的总供应量为2,100,000,000,000,000（2.1千万亿）。",
  whySatoshisMatterLabel: "为什么聪很重要：",
  whySatoshisMatterText:
    '随着比特币价格上涨，人们越来越多地以聪而非整个比特币进行交易。说"500 sats"比说"0.000005 BTC"直观得多。',
  lightningNetworkLabel: "闪电网络：",
  lightningNetworkText:
    "在闪电网络上，支付以聪（甚至毫聪——聪的千分之一）计价。这使得小到一分钱的微支付成为可能。",
  convertingSatoshisTo: "将聪转换为{name}（{code}）",
  conversionExplanation:
    "要将聪转换为{name}，将聪的数量除以100,000,000得到BTC金额，然后乘以当前BTC/{code}汇率。例如：",
  calculatorExplanation:
    "上面的计算器可以即时计算任意金额——只需在聪字段或{code}字段中输入数值，转换就会实时发生。您还可以看到等效的比特币金额。",
  quickReference: "快速参考",
  convertToOtherCurrencies: "将聪转换为其他货币",
};

const ko: TranslationStrings = {
  convertSatoshisTo: "사토시를 변환",
  instantlyConvert: "비트코인 사토시(sats)와 {name}를 즉시 상호 변환",
  satoshisLabel: "사토시 (sats)",
  enterSatoshis: "사토시 금액 입력",
  enterAmountIn: "{code} 금액 입력",
  shareLinkLabel: "이 변환 공유:",
  copyLink: "링크 복사",
  linkCopied: "복사됨!",
  whatIsASatoshi: "사토시란?",
  whatIsASatoshiBody:
    '사토시("sat" 또는 "sats"로 약칭)는 비트코인의 최소 단위입니다. 2008년 10월 31일 비트코인 백서를 발표한 익명의 창시자 사토시 나카모토의 이름을 따서 명명되었습니다. "사토시"라는 용어는 초기 비트코인 커뮤니티에서 비트코인의 아주 작은 부분을 지칭하는 편리한 방법으로 제안되었습니다.',
  howSatoshisWork: "비트코인에서 사토시의 작동 방식",
  fixedSupplyLabel: "고정 공급량:",
  fixedSupplyText:
    "비트코인은 2,100만 개(21,000,000 BTC)만 존재합니다. 이 상한선은 비트코인 프로토콜에 의해 강제되며 변경할 수 없습니다.",
  hundredMillionLabel: "비트코인당 1억 사토시:",
  hundredMillionText:
    "각 비트코인은 100,000,000(1억) 사토시로 나눌 수 있습니다. 즉, 사토시의 총 공급량은 2,100,000,000,000,000(2.1경)입니다.",
  whySatoshisMatterLabel: "사토시가 중요한 이유:",
  whySatoshisMatterText:
    '비트코인 가격이 상승함에 따라 사람들은 비트코인 전체가 아닌 사토시로 거래하는 경우가 점점 더 많아지고 있습니다. "0.000005 BTC"보다 "500 sats"라고 말하는 것이 훨씬 직관적입니다.',
  lightningNetworkLabel: "라이트닝 네트워크:",
  lightningNetworkText:
    "라이트닝 네트워크에서 결제는 사토시(또는 밀리사토시 — 사토시의 천분의 일)로 표시됩니다. 이를 통해 1원 미만의 소액 결제가 가능합니다.",
  convertingSatoshisTo: "사토시를 {name}({code})로 변환",
  conversionExplanation:
    "사토시를 {name}로 변환하려면 사토시 수를 100,000,000으로 나누어 BTC 금액을 구한 다음, 현재 BTC/{code} 환율을 곱합니다. 예를 들어:",
  calculatorExplanation:
    "위의 계산기는 어떤 금액이든 즉시 계산합니다. 사토시 필드 또는 {code} 필드에 값을 입력하기만 하면 실시간으로 변환됩니다. 동등한 비트코인 금액도 표시됩니다.",
  quickReference: "빠른 참조",
  convertToOtherCurrencies: "사토시를 다른 통화로 변환",
};

const hi: TranslationStrings = {
  convertSatoshisTo: "सतोशी को बदलें",
  instantlyConvert:
    "बिटकॉइन सतोशी (sats) को {name} में और इसके विपरीत तुरंत बदलें",
  satoshisLabel: "सतोशी (sats)",
  enterSatoshis: "सतोशी में राशि दर्ज करें",
  enterAmountIn: "{code} में राशि दर्ज करें",
  shareLinkLabel: "यह रूपांतरण साझा करें:",
  copyLink: "लिंक कॉपी करें",
  linkCopied: "लिंक कॉपी हो गया!",
  whatIsASatoshi: "सतोशी क्या है?",
  whatIsASatoshiBody:
    'सतोशी (जिसे अक्सर "sat" या "sats" कहा जाता है) बिटकॉइन की सबसे छोटी इकाई है। इसका नाम बिटकॉइन के छद्म नाम वाले निर्माता सतोशी नाकामोतो के नाम पर रखा गया है, जिन्होंने 31 अक्टूबर, 2008 को बिटकॉइन श्वेतपत्र प्रकाशित किया था। "सतोशी" शब्द प्रारंभिक बिटकॉइन समुदाय द्वारा बिटकॉइन के छोटे अंशों को संदर्भित करने के एक सुविधाजनक तरीके के रूप में प्रस्तावित किया गया था।',
  howSatoshisWork: "बिटकॉइन में सतोशी कैसे काम करते हैं",
  fixedSupplyLabel: "निश्चित आपूर्ति:",
  fixedSupplyText:
    "केवल 21 मिलियन बिटकॉइन (21,000,000 BTC) ही होंगे। यह कठोर सीमा बिटकॉइन प्रोटोकॉल द्वारा लागू है और इसे बदला नहीं जा सकता।",
  hundredMillionLabel: "प्रति बिटकॉइन 10 करोड़ सतोशी:",
  hundredMillionText:
    "प्रत्येक बिटकॉइन 100,000,000 (दस करोड़) सतोशी में विभाज्य है। इसका मतलब है कि सतोशी की कुल आपूर्ति 2,100,000,000,000,000 (2.1 पद्म) है।",
  whySatoshisMatterLabel: "सतोशी क्यों मायने रखते हैं:",
  whySatoshisMatterText:
    'जैसे-जैसे बिटकॉइन की कीमत बढ़ती है, लोग पूरे बिटकॉइन के बजाय सतोशी में लेनदेन करते हैं। "0.000005 BTC" कहने से "500 sats" कहना कहीं अधिक सहज है।',
  lightningNetworkLabel: "लाइटनिंग नेटवर्क:",
  lightningNetworkText:
    "लाइटनिंग नेटवर्क पर, भुगतान सतोशी (या मिलीसतोशी — सतोशी का हजारवां हिस्सा) में होते हैं। यह एक पैसे के अंश जितने छोटे सूक्ष्म भुगतान को संभव बनाता है।",
  convertingSatoshisTo: "सतोशी को {name} ({code}) में बदलें",
  conversionExplanation:
    "सतोशी को {name} में बदलने के लिए, सतोशी की संख्या को 100,000,000 से विभाजित करके BTC राशि प्राप्त करें, फिर वर्तमान BTC/{code} विनिमय दर से गुणा करें। उदाहरण के लिए:",
  calculatorExplanation:
    "ऊपर का कैलकुलेटर किसी भी राशि के लिए यह गणना तुरंत करता है — बस सतोशी फ़ील्ड या {code} फ़ील्ड में मान टाइप करें और रूपांतरण वास्तविक समय में होता है। आप समकक्ष बिटकॉइन राशि भी देखेंगे।",
  quickReference: "त्वरित संदर्भ",
  convertToOtherCurrencies: "सतोशी को अन्य मुद्राओं में बदलें",
};

const tr: TranslationStrings = {
  convertSatoshisTo: "Satoshi'yi dönüştür",
  instantlyConvert:
    "Bitcoin satoshi'lerini (sats) anında {name}'ye ve tersine dönüştürün",
  satoshisLabel: "Satoshi (sats)",
  enterSatoshis: "Satoshi miktarı girin",
  enterAmountIn: "{code} miktarı girin",
  shareLinkLabel: "Bu dönüşümü paylaş:",
  copyLink: "Bağlantıyı kopyala",
  linkCopied: "Kopyalandı!",
  whatIsASatoshi: "Satoshi Nedir?",
  whatIsASatoshiBody:
    'Satoshi (genellikle "sat" veya "sats" olarak kısaltılır) Bitcoin\'in en küçük birimidir. Bitcoin\'in takma adlı yaratıcısı Satoshi Nakamoto\'nun adını almıştır. Nakamoto, Bitcoin teknik raporunu 31 Ekim 2008\'de yayınladı. "Satoshi" terimi, erken Bitcoin topluluğu tarafından Bitcoin\'in küçük kesirlerine atıfta bulunmanın uygun bir yolu olarak önerildi.',
  howSatoshisWork: "Satoshiler Bitcoin'de Nasıl Çalışır",
  fixedSupplyLabel: "Sabit arz:",
  fixedSupplyText:
    "Toplam 21 milyon Bitcoin (21.000.000 BTC) olacaktır. Bu üst sınır Bitcoin protokolü tarafından uygulanır ve değiştirilemez.",
  hundredMillionLabel: "Bitcoin başına 100 milyon Sat:",
  hundredMillionText:
    "Her Bitcoin 100.000.000 (yüz milyon) satoshiye bölünebilir. Bu, toplam satoshi arzının 2.100.000.000.000.000 (2,1 katrilyon) olduğu anlamına gelir.",
  whySatoshisMatterLabel: "Satoshiler Neden Önemlidir:",
  whySatoshisMatterText:
    'Bitcoin fiyatı yükseldikçe, insanlar tam Bitcoin yerine satoshilerle işlem yapmaya başlamıştır. "0,000005 BTC" demektense "500 sats" demek çok daha sezgiseldir.',
  lightningNetworkLabel: "Lightning Ağı:",
  lightningNetworkText:
    "Lightning Ağı'nda ödemeler satoshi (hatta millisatoshi — satoshinin binde biri) cinsinden yapılır. Bu, bir kuruşun kesiri kadar küçük mikroödemeleri mümkün kılar.",
  convertingSatoshisTo: "Satoshi'yi {name} ({code})'ye Dönüştürme",
  conversionExplanation:
    "Satoshi'yi {name}'ye dönüştürmek için satoshi sayısını 100.000.000'a bölerek BTC miktarını bulun, ardından güncel BTC/{code} döviz kuruyla çarpın. Örneğin:",
  calculatorExplanation:
    "Yukarıdaki hesap makinesi herhangi bir miktar için bu hesaplamayı anında yapar — sadece satoshi alanına veya {code} alanına bir değer yazın ve dönüşüm gerçek zamanlı olarak gerçekleşir. Eşdeğer Bitcoin miktarını da göreceksiniz.",
  quickReference: "Hızlı Referans",
  convertToOtherCurrencies: "Satoshi'yi diğer para birimlerine dönüştür",
};

const ru: TranslationStrings = {
  convertSatoshisTo: "Конвертировать сатоши в",
  instantlyConvert:
    "Мгновенно конвертируйте биткоин-сатоши (sats) в {name} и обратно",
  satoshisLabel: "Сатоши (sats)",
  enterSatoshis: "Введите количество сатоши",
  enterAmountIn: "Введите сумму в {code}",
  shareLinkLabel: "Поделиться конвертацией:",
  copyLink: "Копировать ссылку",
  linkCopied: "Скопировано!",
  whatIsASatoshi: "Что такое сатоши?",
  whatIsASatoshiBody:
    'Сатоши (часто сокращается как "sat" или "sats") — это наименьшая единица биткоина. Назван в честь Сатоши Накамото, псевдонимного создателя биткоина, опубликовавшего белую книгу биткоина 31 октября 2008 года. Термин "сатоши" был предложен ранним сообществом биткоина как удобный способ ссылаться на крошечные доли биткоина.',
  howSatoshisWork: "Как работают сатоши в биткоине",
  fixedSupplyLabel: "Фиксированное предложение:",
  fixedSupplyText:
    "Биткоинов будет существовать только 21 миллион (21 000 000 BTC). Этот верхний предел обеспечивается протоколом биткоина и не может быть изменён.",
  hundredMillionLabel: "100 миллионов сатоши в биткоине:",
  hundredMillionText:
    "Каждый биткоин делится на 100 000 000 (сто миллионов) сатоши. Это означает, что общее количество сатоши составляет 2 100 000 000 000 000 (2,1 квадриллиона).",
  whySatoshisMatterLabel: "Почему сатоши важны:",
  whySatoshisMatterText:
    'По мере роста цены биткоина люди всё чаще совершают транзакции в сатоши, а не в целых биткоинах. Говорить "500 sats" гораздо удобнее, чем "0,000005 BTC".',
  lightningNetworkLabel: "Сеть Lightning:",
  lightningNetworkText:
    "В сети Lightning платежи выражаются в сатоши (или даже миллисатоши — тысячных долях сатоши). Это делает возможными микроплатежи размером в доли копейки.",
  convertingSatoshisTo: "Конвертация сатоши в {name} ({code})",
  conversionExplanation:
    "Чтобы конвертировать сатоши в {name}, разделите количество сатоши на 100 000 000 для получения суммы в BTC, затем умножьте на текущий курс BTC/{code}. Например:",
  calculatorExplanation:
    "Калькулятор выше мгновенно выполняет этот расчёт для любой суммы — просто введите значение в поле сатоши или в поле {code}, и конвертация произойдёт в реальном времени. Вы также увидите эквивалентную сумму в биткоинах.",
  quickReference: "Краткий справочник",
  convertToOtherCurrencies: "Конвертировать сатоши в другие валюты",
};

const ar: TranslationStrings = {
  convertSatoshisTo: "تحويل ساتوشي إلى",
  instantlyConvert: "حوّل ساتوشي البيتكوين (sats) إلى {name} والعكس فوراً",
  satoshisLabel: "ساتوشي (sats)",
  enterSatoshis: "أدخل المبلغ بالساتوشي",
  enterAmountIn: "أدخل المبلغ بـ {code}",
  shareLinkLabel: "مشاركة هذا التحويل:",
  copyLink: "نسخ الرابط",
  linkCopied: "تم النسخ!",
  whatIsASatoshi: "ما هو الساتوشي؟",
  whatIsASatoshiBody:
    'الساتوشي (يُختصر غالباً بـ "sat" أو "sats") هو أصغر وحدة في البيتكوين. سُمي على اسم ساتوشي ناكاموتو، المنشئ المجهول للبيتكوين، الذي نشر ورقة البيتكوين البيضاء في 31 أكتوبر 2008. اقتُرح مصطلح "ساتوشي" من قبل مجتمع البيتكوين المبكر كطريقة مريحة للإشارة إلى أجزاء صغيرة جداً من البيتكوين.',
  howSatoshisWork: "كيف يعمل الساتوشي في البيتكوين",
  fixedSupplyLabel: "عرض ثابت:",
  fixedSupplyText:
    "لن يوجد سوى 21 مليون بيتكوين (21,000,000 BTC). هذا الحد الأقصى يفرضه بروتوكول البيتكوين ولا يمكن تغييره.",
  hundredMillionLabel: "100 مليون ساتوشي لكل بيتكوين:",
  hundredMillionText:
    "كل بيتكوين قابل للقسمة إلى 100,000,000 (مائة مليون) ساتوشي. هذا يعني أن إجمالي عرض الساتوشي هو 2,100,000,000,000,000 (2.1 كوادريليون).",
  whySatoshisMatterLabel: "لماذا الساتوشي مهم:",
  whySatoshisMatterText:
    'مع ارتفاع سعر البيتكوين، يتعامل الناس بشكل متزايد بالساتوشي بدلاً من البيتكوين الكامل. قول "500 sats" أكثر سهولة من قول "0.000005 BTC".',
  lightningNetworkLabel: "شبكة Lightning:",
  lightningNetworkText:
    "في شبكة Lightning، تُقدر المدفوعات بالساتوشي (أو حتى ميلي ساتوشي — أجزاء من الألف). هذا يجعل المدفوعات الصغيرة بحجم جزء من قرش ممكنة.",
  convertingSatoshisTo: "تحويل الساتوشي إلى {name} ({code})",
  conversionExplanation:
    "لتحويل الساتوشي إلى {name}، اقسم عدد الساتوشي على 100,000,000 للحصول على المبلغ بالـ BTC، ثم اضرب في سعر الصرف الحالي BTC/{code}. على سبيل المثال:",
  calculatorExplanation:
    "الآلة الحاسبة أعلاه تقوم بهذا الحساب فوراً لأي مبلغ — فقط اكتب قيمة في حقل الساتوشي أو حقل {code} ويحدث التحويل في الوقت الفعلي. سترى أيضاً المبلغ المعادل بالبيتكوين.",
  quickReference: "مرجع سريع",
  convertToOtherCurrencies: "تحويل الساتوشي إلى عملات أخرى",
};

const de: TranslationStrings = {
  convertSatoshisTo: "Satoshis umrechnen in",
  instantlyConvert:
    "Bitcoin-Satoshis (Sats) sofort in {name} und zurück umrechnen",
  satoshisLabel: "Satoshis (Sats)",
  enterSatoshis: "Betrag in Satoshis eingeben",
  enterAmountIn: "Betrag in {code} eingeben",
  shareLinkLabel: "Diese Umrechnung teilen:",
  copyLink: "Link kopieren",
  linkCopied: "Kopiert!",
  whatIsASatoshi: "Was ist ein Satoshi?",
  whatIsASatoshiBody:
    'Ein Satoshi (oft als "Sat" oder "Sats" abgekürzt) ist die kleinste Einheit von Bitcoin. Er ist nach Satoshi Nakamoto benannt, dem pseudonymen Schöpfer von Bitcoin, der am 31. Oktober 2008 das Bitcoin-Whitepaper veröffentlichte. Der Begriff "Satoshi" wurde von der frühen Bitcoin-Gemeinschaft als praktische Bezeichnung für winzige Bruchteile eines Bitcoins vorgeschlagen.',
  howSatoshisWork: "Wie Satoshis in Bitcoin funktionieren",
  fixedSupplyLabel: "Feste Obergrenze:",
  fixedSupplyText:
    "Es wird nur 21 Millionen Bitcoin (21.000.000 BTC) geben. Diese Obergrenze wird vom Bitcoin-Protokoll durchgesetzt und kann nicht geändert werden.",
  hundredMillionLabel: "100 Millionen Sats pro Bitcoin:",
  hundredMillionText:
    "Jeder Bitcoin ist in 100.000.000 (einhundert Millionen) Satoshis teilbar. Das bedeutet, das Gesamtangebot an Satoshis beträgt 2.100.000.000.000.000 (2,1 Billiarden).",
  whySatoshisMatterLabel: "Warum Satoshis wichtig sind:",
  whySatoshisMatterText:
    'Da der Bitcoin-Preis steigt, handeln Menschen zunehmend in Satoshis statt in ganzen Bitcoins. Es ist viel intuitiver, "500 Sats" zu sagen als "0,000005 BTC".',
  lightningNetworkLabel: "Lightning-Netzwerk:",
  lightningNetworkText:
    "Im Lightning-Netzwerk werden Zahlungen in Satoshis (oder sogar Millisatoshis — Tausendstel eines Satoshis) angegeben. Dies ermöglicht Mikrozahlungen, die so klein wie ein Bruchteil eines Cents sind.",
  convertingSatoshisTo: "Satoshis in {name} ({code}) umrechnen",
  conversionExplanation:
    "Um Satoshis in {name} umzurechnen, teilen Sie die Anzahl der Satoshis durch 100.000.000, um den BTC-Betrag zu erhalten, und multiplizieren Sie dann mit dem aktuellen BTC/{code}-Wechselkurs. Zum Beispiel:",
  calculatorExplanation:
    "Der obige Rechner führt diese Berechnung sofort für jeden Betrag durch — geben Sie einfach einen Wert in das Satoshi-Feld oder das {code}-Feld ein, und die Umrechnung erfolgt in Echtzeit. Sie sehen auch den entsprechenden Bitcoin-Betrag.",
  quickReference: "Kurzübersicht",
  convertToOtherCurrencies: "Satoshis in andere Währungen umrechnen",
};

const fr: TranslationStrings = {
  convertSatoshisTo: "Convertir des Satoshis en",
  instantlyConvert:
    "Convertissez instantanément des satoshis Bitcoin (sats) en {name} et inversement",
  satoshisLabel: "Satoshis (sats)",
  enterSatoshis: "Entrer le montant en satoshis",
  enterAmountIn: "Entrer le montant en {code}",
  shareLinkLabel: "Partager cette conversion :",
  copyLink: "Copier le lien",
  linkCopied: "Lien copié !",
  whatIsASatoshi: "Qu'est-ce qu'un Satoshi ?",
  whatIsASatoshiBody:
    "Un satoshi (souvent abrégé en « sat » ou « sats ») est la plus petite unité du Bitcoin. Il porte le nom de Satoshi Nakamoto, le créateur pseudonyme du Bitcoin, qui a publié le livre blanc du Bitcoin le 31 octobre 2008. Le terme « satoshi » a été proposé par la communauté Bitcoin primitive comme un moyen pratique de désigner de minuscules fractions d'un Bitcoin.",
  howSatoshisWork: "Comment fonctionnent les Satoshis dans Bitcoin",
  fixedSupplyLabel: "Offre fixe :",
  fixedSupplyText:
    "Il n'y aura jamais que 21 millions de Bitcoin (21 000 000 BTC). Ce plafond est imposé par le protocole Bitcoin et ne peut pas être modifié.",
  hundredMillionLabel: "100 millions de Sats par Bitcoin :",
  hundredMillionText:
    "Chaque Bitcoin est divisible en 100 000 000 (cent millions) de satoshis. Cela signifie que l'offre totale de satoshis est de 2 100 000 000 000 000 (2,1 billiards).",
  whySatoshisMatterLabel: "Pourquoi les Satoshis sont importants :",
  whySatoshisMatterText:
    "À mesure que le prix du Bitcoin augmente, les gens effectuent de plus en plus de transactions en satoshis plutôt qu'en Bitcoins entiers. Il est beaucoup plus intuitif de dire « 500 sats » que « 0,000005 BTC ».",
  lightningNetworkLabel: "Réseau Lightning :",
  lightningNetworkText:
    "Sur le réseau Lightning, les paiements sont libellés en satoshis (ou même en millisatoshis — millièmes de satoshi). Cela rend possible des micropaiements aussi petits qu'une fraction de centime.",
  convertingSatoshisTo: "Convertir des Satoshis en {name} ({code})",
  conversionExplanation:
    "Pour convertir des satoshis en {name}, divisez le nombre de satoshis par 100 000 000 pour obtenir le montant en BTC, puis multipliez par le taux de change actuel BTC/{code}. Par exemple :",
  calculatorExplanation:
    "Le calculateur ci-dessus effectue ce calcul instantanément pour n'importe quel montant — tapez simplement une valeur dans le champ satoshis ou dans le champ {code} et la conversion se fait en temps réel. Vous verrez également le montant équivalent en Bitcoin.",
  quickReference: "Référence rapide",
  convertToOtherCurrencies: "Convertir des Satoshis en d'autres devises",
};

const th: TranslationStrings = {
  convertSatoshisTo: "แปลงซาโตชิเป็น",
  instantlyConvert: "แปลงซาโตชิบิตคอยน์ (sats) เป็น{name}และกลับกันทันที",
  satoshisLabel: "ซาโตชิ (sats)",
  enterSatoshis: "ใส่จำนวนซาโตชิ",
  enterAmountIn: "ใส่จำนวนเงินเป็น {code}",
  shareLinkLabel: "แชร์การแปลงนี้:",
  copyLink: "คัดลอกลิงก์",
  linkCopied: "คัดลอกแล้ว!",
  whatIsASatoshi: "ซาโตชิคืออะไร?",
  whatIsASatoshiBody:
    'ซาโตชิ (มักย่อว่า "sat" หรือ "sats") คือหน่วยที่เล็กที่สุดของบิตคอยน์ ตั้งชื่อตามซาโตชิ นากาโมโตะ ผู้สร้างบิตคอยน์ที่ใช้นามแฝง ซึ่งเผยแพร่สมุดปกขาวบิตคอยน์เมื่อวันที่ 31 ตุลาคม 2008 คำว่า "ซาโตชิ" ถูกเสนอโดยชุมชนบิตคอยน์ยุคแรกเพื่อใช้เรียกเศษเสี้ยวเล็กๆ ของบิตคอยน์',
  howSatoshisWork: "ซาโตชิทำงานอย่างไรในบิตคอยน์",
  fixedSupplyLabel: "อุปทานคงที่:",
  fixedSupplyText:
    "จะมีบิตคอยน์เพียง 21 ล้านเหรียญ (21,000,000 BTC) เท่านั้น ขีดจำกัดนี้บังคับใช้โดยโปรโตคอลบิตคอยน์และไม่สามารถเปลี่ยนแปลงได้",
  hundredMillionLabel: "100 ล้านซาโตชิต่อบิตคอยน์:",
  hundredMillionText:
    "บิตคอยน์แต่ละเหรียญแบ่งได้เป็น 100,000,000 (หนึ่งร้อยล้าน) ซาโตชิ ซึ่งหมายความว่าอุปทานรวมของซาโตชิคือ 2,100,000,000,000,000 (2.1 พันล้านล้าน)",
  whySatoshisMatterLabel: "ทำไมซาโตชิจึงสำคัญ:",
  whySatoshisMatterText:
    'เมื่อราคาบิตคอยน์สูงขึ้น ผู้คนทำธุรกรรมเป็นซาโตชิมากขึ้นแทนที่จะเป็นบิตคอยน์ทั้งเหรียญ การพูดว่า "500 sats" สื่อความหมายได้ง่ายกว่า "0.000005 BTC" มาก',
  lightningNetworkLabel: "เครือข่าย Lightning:",
  lightningNetworkText:
    "บนเครือข่าย Lightning การชำระเงินจะระบุเป็นซาโตชิ (หรือแม้แต่มิลลิซาโตชิ — หนึ่งในพันของซาโตชิ) ทำให้สามารถชำระเงินขนาดเล็กเท่ากับเศษสตางค์ได้",
  convertingSatoshisTo: "แปลงซาโตชิเป็น {name} ({code})",
  conversionExplanation:
    "ในการแปลงซาโตชิเป็น {name} ให้หารจำนวนซาโตชิด้วย 100,000,000 เพื่อได้จำนวน BTC จากนั้นคูณด้วยอัตราแลกเปลี่ยน BTC/{code} ปัจจุบัน ตัวอย่าง:",
  calculatorExplanation:
    "เครื่องคิดเลขด้านบนทำการคำนวณนี้ทันทีสำหรับจำนวนเท่าใดก็ได้ — แค่พิมพ์ค่าในฟิลด์ซาโตชิหรือฟิลด์ {code} แล้วการแปลงจะเกิดขึ้นแบบเรียลไทม์ คุณจะเห็นจำนวนบิตคอยน์ที่เทียบเท่าด้วย",
  quickReference: "อ้างอิงด่วน",
  convertToOtherCurrencies: "แปลงซาโตชิเป็นสกุลเงินอื่น",
};

const pl: TranslationStrings = {
  convertSatoshisTo: "Przelicz Satoshi na",
  instantlyConvert:
    "Natychmiast przelicz bitcoinowe satoshi (sats) na {name} i z powrotem",
  satoshisLabel: "Satoshi (sats)",
  enterSatoshis: "Wpisz kwotę w satoshi",
  enterAmountIn: "Wpisz kwotę w {code}",
  shareLinkLabel: "Udostępnij tę konwersję:",
  copyLink: "Kopiuj link",
  linkCopied: "Skopiowano!",
  whatIsASatoshi: "Czym jest Satoshi?",
  whatIsASatoshiBody:
    'Satoshi (często skracane do "sat" lub "sats") to najmniejsza jednostka Bitcoina. Nazwa pochodzi od Satoshi Nakamoto, pseudonimowego twórcy Bitcoina, który opublikował białą księgę Bitcoina 31 października 2008 roku. Termin "satoshi" został zaproponowany przez wczesną społeczność Bitcoina jako wygodny sposób na określenie niewielkich ułamków Bitcoina.',
  howSatoshisWork: "Jak działają Satoshi w Bitcoinie",
  fixedSupplyLabel: "Stała podaż:",
  fixedSupplyText:
    "Będzie istnieć tylko 21 milionów Bitcoinów (21 000 000 BTC). Ten twardy limit jest wymuszany przez protokół Bitcoin i nie może zostać zmieniony.",
  hundredMillionLabel: "100 milionów Satoshi na Bitcoina:",
  hundredMillionText:
    "Każdy Bitcoin jest podzielny na 100 000 000 (sto milionów) satoshi. Oznacza to, że całkowita podaż satoshi wynosi 2 100 000 000 000 000 (2,1 biliarda).",
  whySatoshisMatterLabel: "Dlaczego Satoshi są ważne:",
  whySatoshisMatterText:
    'Wraz ze wzrostem ceny Bitcoina ludzie coraz częściej dokonują transakcji w satoshi zamiast w całych Bitcoinach. Powiedzenie "500 sats" jest znacznie bardziej intuicyjne niż "0,000005 BTC".',
  lightningNetworkLabel: "Sieć Lightning:",
  lightningNetworkText:
    "W sieci Lightning płatności są wyrażane w satoshi (a nawet w millisatoshi — tysięcznych częściach satoshi). Umożliwia to mikropłatności tak małe jak ułamek grosza.",
  convertingSatoshisTo: "Przeliczanie Satoshi na {name} ({code})",
  conversionExplanation:
    "Aby przeliczyć satoshi na {name}, podziel liczbę satoshi przez 100 000 000, aby uzyskać kwotę w BTC, a następnie pomnóż przez aktualny kurs BTC/{code}. Na przykład:",
  calculatorExplanation:
    "Kalkulator powyżej wykonuje te obliczenia natychmiast dla dowolnej kwoty — wystarczy wpisać wartość w pole satoshi lub pole {code}, a konwersja nastąpi w czasie rzeczywistym. Zobaczysz także równowartość w Bitcoinie.",
  quickReference: "Szybka ściągawka",
  convertToOtherCurrencies: "Przelicz Satoshi na inne waluty",
};

const uk: TranslationStrings = {
  convertSatoshisTo: "Конвертувати сатоші в",
  instantlyConvert:
    "Миттєво конвертуйте біткоін-сатоші (sats) у {name} і навпаки",
  satoshisLabel: "Сатоші (sats)",
  enterSatoshis: "Введіть кількість сатоші",
  enterAmountIn: "Введіть суму в {code}",
  shareLinkLabel: "Поділитися конвертацією:",
  copyLink: "Копіювати посилання",
  linkCopied: "Скопійовано!",
  whatIsASatoshi: "Що таке сатоші?",
  whatIsASatoshiBody:
    'Сатоші (часто скорочується як "sat" або "sats") — це найменша одиниця біткоіна. Названий на честь Сатоші Накамото, псевдонімного творця біткоіна, який опублікував білу книгу біткоіна 31 жовтня 2008 року. Термін "сатоші" був запропонований ранньою спільнотою біткоіна як зручний спосіб посилатися на крихітні частки біткоіна.',
  howSatoshisWork: "Як працюють сатоші в біткоіні",
  fixedSupplyLabel: "Фіксована пропозиція:",
  fixedSupplyText:
    "Існуватиме лише 21 мільйон біткоінів (21 000 000 BTC). Ця верхня межа забезпечується протоколом біткоіна і не може бути змінена.",
  hundredMillionLabel: "100 мільйонів сатоші на біткоін:",
  hundredMillionText:
    "Кожен біткоін ділиться на 100 000 000 (сто мільйонів) сатоші. Це означає, що загальна кількість сатоші становить 2 100 000 000 000 000 (2,1 квадрильйона).",
  whySatoshisMatterLabel: "Чому сатоші важливі:",
  whySatoshisMatterText:
    'У міру зростання ціни біткоіна люди все частіше здійснюють транзакції в сатоші, а не в цілих біткоінах. Говорити "500 sats" набагато зручніше, ніж "0,000005 BTC".',
  lightningNetworkLabel: "Мережа Lightning:",
  lightningNetworkText:
    "У мережі Lightning платежі виражаються в сатоші (або навіть мілісатоші — тисячних частках сатоші). Це робить можливими мікроплатежі розміром у частки копійки.",
  convertingSatoshisTo: "Конвертація сатоші в {name} ({code})",
  conversionExplanation:
    "Щоб конвертувати сатоші в {name}, поділіть кількість сатоші на 100 000 000, щоб отримати суму в BTC, потім помножте на поточний курс BTC/{code}. Наприклад:",
  calculatorExplanation:
    "Калькулятор вище миттєво виконує цей розрахунок для будь-якої суми — просто введіть значення в поле сатоші або поле {code}, і конвертація відбудеться в реальному часі. Ви також побачите еквівалентну суму в біткоінах.",
  quickReference: "Коротка довідка",
  convertToOtherCurrencies: "Конвертувати сатоші в інші валюти",
};

const id: TranslationStrings = {
  convertSatoshisTo: "Konversi Satoshi ke",
  instantlyConvert:
    "Konversi instan satoshi Bitcoin (sats) ke {name} dan sebaliknya",
  satoshisLabel: "Satoshi (sats)",
  enterSatoshis: "Masukkan jumlah dalam satoshi",
  enterAmountIn: "Masukkan jumlah dalam {code}",
  shareLinkLabel: "Bagikan konversi ini:",
  copyLink: "Salin tautan",
  linkCopied: "Tersalin!",
  whatIsASatoshi: "Apa itu Satoshi?",
  whatIsASatoshiBody:
    'Satoshi (sering disingkat "sat" atau "sats") adalah unit terkecil Bitcoin. Dinamai setelah Satoshi Nakamoto, pencipta pseudonim Bitcoin, yang menerbitkan whitepaper Bitcoin pada 31 Oktober 2008. Istilah "satoshi" diusulkan oleh komunitas Bitcoin awal sebagai cara yang nyaman untuk merujuk pada pecahan kecil Bitcoin.',
  howSatoshisWork: "Cara Satoshi Bekerja dalam Bitcoin",
  fixedSupplyLabel: "Pasokan tetap:",
  fixedSupplyText:
    "Hanya akan ada 21 juta Bitcoin (21.000.000 BTC). Batas maksimum ini ditegakkan oleh protokol Bitcoin dan tidak dapat diubah.",
  hundredMillionLabel: "100 juta Sats per Bitcoin:",
  hundredMillionText:
    "Setiap Bitcoin dapat dibagi menjadi 100.000.000 (seratus juta) satoshi. Ini berarti total pasokan satoshi adalah 2.100.000.000.000.000 (2,1 kuadriliun).",
  whySatoshisMatterLabel: "Mengapa Satoshi Penting:",
  whySatoshisMatterText:
    'Seiring naiknya harga Bitcoin, orang semakin banyak bertransaksi dalam satoshi daripada Bitcoin utuh. Mengatakan "500 sats" jauh lebih intuitif daripada "0,000005 BTC".',
  lightningNetworkLabel: "Lightning Network:",
  lightningNetworkText:
    "Di Lightning Network, pembayaran didenominasikan dalam satoshi (atau bahkan millisatoshi — seperseribu satoshi). Ini memungkinkan pembayaran mikro sekecil pecahan sen.",
  convertingSatoshisTo: "Konversi Satoshi ke {name} ({code})",
  conversionExplanation:
    "Untuk mengonversi satoshi ke {name}, bagi jumlah satoshi dengan 100.000.000 untuk mendapatkan jumlah BTC, lalu kalikan dengan kurs BTC/{code} saat ini. Contoh:",
  calculatorExplanation:
    "Kalkulator di atas melakukan perhitungan ini secara instan untuk jumlah berapa pun — cukup ketik nilai di kolom satoshi atau kolom {code} dan konversi terjadi secara real-time. Anda juga akan melihat jumlah Bitcoin yang setara.",
  quickReference: "Referensi Cepat",
  convertToOtherCurrencies: "Konversi Satoshi ke mata uang lain",
};

const vi: TranslationStrings = {
  convertSatoshisTo: "Đổi Satoshi sang",
  instantlyConvert: "Đổi ngay satoshi Bitcoin (sats) sang {name} và ngược lại",
  satoshisLabel: "Satoshi (sats)",
  enterSatoshis: "Nhập số lượng satoshi",
  enterAmountIn: "Nhập số tiền bằng {code}",
  shareLinkLabel: "Chia sẻ quy đổi này:",
  copyLink: "Sao chép liên kết",
  linkCopied: "Đã sao chép!",
  whatIsASatoshi: "Satoshi là gì?",
  whatIsASatoshiBody:
    'Satoshi (thường được viết tắt là "sat" hoặc "sats") là đơn vị nhỏ nhất của Bitcoin. Nó được đặt theo tên của Satoshi Nakamoto, người tạo ra Bitcoin dưới bút danh, người đã công bố bản whitepaper Bitcoin vào ngày 31 tháng 10 năm 2008. Thuật ngữ "satoshi" được cộng đồng Bitcoin ban đầu đề xuất như một cách thuận tiện để chỉ những phần nhỏ của Bitcoin.',
  howSatoshisWork: "Satoshi hoạt động như thế nào trong Bitcoin",
  fixedSupplyLabel: "Nguồn cung cố định:",
  fixedSupplyText:
    "Chỉ có 21 triệu Bitcoin (21.000.000 BTC). Giới hạn cứng này được thực thi bởi giao thức Bitcoin và không thể thay đổi.",
  hundredMillionLabel: "100 triệu Sats mỗi Bitcoin:",
  hundredMillionText:
    "Mỗi Bitcoin có thể chia thành 100.000.000 (một trăm triệu) satoshi. Điều này có nghĩa là tổng cung satoshi là 2.100.000.000.000.000 (2,1 triệu tỷ).",
  whySatoshisMatterLabel: "Tại sao Satoshi quan trọng:",
  whySatoshisMatterText:
    'Khi giá Bitcoin tăng, mọi người ngày càng giao dịch bằng satoshi thay vì Bitcoin nguyên. Nói "500 sats" trực quan hơn nhiều so với "0,000005 BTC".',
  lightningNetworkLabel: "Mạng Lightning:",
  lightningNetworkText:
    "Trên mạng Lightning, các khoản thanh toán được tính bằng satoshi (hoặc thậm chí millisatoshi — phần nghìn của satoshi). Điều này cho phép thanh toán vi mô nhỏ như một phần xu.",
  convertingSatoshisTo: "Đổi Satoshi sang {name} ({code})",
  conversionExplanation:
    "Để đổi satoshi sang {name}, chia số satoshi cho 100.000.000 để có số BTC, rồi nhân với tỷ giá BTC/{code} hiện tại. Ví dụ:",
  calculatorExplanation:
    "Máy tính ở trên thực hiện phép tính này ngay lập tức cho bất kỳ số tiền nào — chỉ cần nhập giá trị vào trường satoshi hoặc trường {code} và quy đổi diễn ra theo thời gian thực. Bạn cũng sẽ thấy số Bitcoin tương đương.",
  quickReference: "Tham khảo nhanh",
  convertToOtherCurrencies: "Đổi Satoshi sang các loại tiền khác",
};

const bn: TranslationStrings = {
  convertSatoshisTo: "সাতোশি রূপান্তর করুন",
  instantlyConvert:
    "বিটকয়েন সাতোশি (sats) কে {name} তে তাৎক্ষণিকভাবে রূপান্তর করুন",
  satoshisLabel: "সাতোশি (sats)",
  enterSatoshis: "সাতোশিতে পরিমাণ লিখুন",
  enterAmountIn: "{code} তে পরিমাণ লিখুন",
  shareLinkLabel: "এই রূপান্তর শেয়ার করুন:",
  copyLink: "লিংক কপি করুন",
  linkCopied: "কপি হয়েছে!",
  whatIsASatoshi: "সাতোশি কী?",
  whatIsASatoshiBody:
    'সাতোশি (প্রায়ই "sat" বা "sats" হিসাবে সংক্ষেপিত) হল বিটকয়েনের ক্ষুদ্রতম একক। এটি সাতোশি নাকামোটোর নামে নামকরণ করা হয়েছে, বিটকয়েনের ছদ্মনামী স্রষ্টা, যিনি ২০০৮ সালের ৩১ অক্টোবর বিটকয়েন হোয়াইটপেপার প্রকাশ করেছিলেন।',
  howSatoshisWork: "বিটকয়েনে সাতোশি কীভাবে কাজ করে",
  fixedSupplyLabel: "নির্দিষ্ট সরবরাহ:",
  fixedSupplyText:
    "মাত্র ২১ মিলিয়ন বিটকয়েন (২১,০০০,০০০ BTC) থাকবে। এই উচ্চসীমা বিটকয়েন প্রোটোকল দ্বারা প্রয়োগ করা হয় এবং পরিবর্তন করা যায় না।",
  hundredMillionLabel: "প্রতি বিটকয়েনে ১০ কোটি সাতোশি:",
  hundredMillionText:
    "প্রতিটি বিটকয়েন ১০০,০০০,০০০ (দশ কোটি) সাতোশিতে বিভাজ্য।",
  whySatoshisMatterLabel: "সাতোশি কেন গুরুত্বপূর্ণ:",
  whySatoshisMatterText:
    'বিটকয়েনের দাম বাড়ার সাথে সাথে মানুষ সম্পূর্ণ বিটকয়েনের পরিবর্তে সাতোশিতে লেনদেন করছে। "500 sats" বলা "0.000005 BTC" বলার চেয়ে অনেক বেশি স্বজ্ঞাত।',
  lightningNetworkLabel: "লাইটনিং নেটওয়ার্ক:",
  lightningNetworkText:
    "লাইটনিং নেটওয়ার্কে, পেমেন্ট সাতোশিতে (বা মিলিসাতোশিতে) হয়। এটি এক পয়সার ভগ্নাংশের মতো ক্ষুদ্র মাইক্রোপেমেন্ট সম্ভব করে তোলে।",
  convertingSatoshisTo: "সাতোশি কে {name} ({code}) তে রূপান্তর",
  conversionExplanation:
    "সাতোশি কে {name} তে রূপান্তর করতে, সাতোশির সংখ্যাকে ১০০,০০০,০০০ দিয়ে ভাগ করে BTC পরিমাণ পান, তারপর বর্তমান BTC/{code} বিনিময় হার দিয়ে গুণ করুন। উদাহরণ:",
  calculatorExplanation:
    "উপরের ক্যালকুলেটর যেকোনো পরিমাণের জন্য তাৎক্ষণিকভাবে এই গণনা করে — শুধু সাতোশি ফিল্ড বা {code} ফিল্ডে মান টাইপ করুন এবং রূপান্তর রিয়েল-টাইমে ঘটবে।",
  quickReference: "দ্রুত রেফারেন্স",
  convertToOtherCurrencies: "সাতোশি অন্যান্য মুদ্রায় রূপান্তর করুন",
};

const ms: TranslationStrings = {
  convertSatoshisTo: "Tukar Satoshi ke",
  instantlyConvert:
    "Tukar satoshi Bitcoin (sats) ke {name} dan sebaliknya dengan serta-merta",
  satoshisLabel: "Satoshi (sats)",
  enterSatoshis: "Masukkan jumlah dalam satoshi",
  enterAmountIn: "Masukkan jumlah dalam {code}",
  shareLinkLabel: "Kongsi penukaran ini:",
  copyLink: "Salin pautan",
  linkCopied: "Disalin!",
  whatIsASatoshi: "Apa itu Satoshi?",
  whatIsASatoshiBody:
    'Satoshi (sering disingkat sebagai "sat" atau "sats") ialah unit terkecil Bitcoin. Ia dinamakan sempena Satoshi Nakamoto, pencipta pseudonim Bitcoin, yang menerbitkan kertas putih Bitcoin pada 31 Oktober 2008.',
  howSatoshisWork: "Bagaimana Satoshi Berfungsi dalam Bitcoin",
  fixedSupplyLabel: "Bekalan tetap:",
  fixedSupplyText:
    "Hanya akan ada 21 juta Bitcoin (21,000,000 BTC). Had maksimum ini dikuatkuasakan oleh protokol Bitcoin dan tidak boleh diubah.",
  hundredMillionLabel: "100 juta Sats setiap Bitcoin:",
  hundredMillionText:
    "Setiap Bitcoin boleh dibahagikan kepada 100,000,000 (seratus juta) satoshi.",
  whySatoshisMatterLabel: "Mengapa Satoshi Penting:",
  whySatoshisMatterText:
    'Apabila harga Bitcoin meningkat, orang ramai semakin banyak bertransaksi dalam satoshi berbanding Bitcoin penuh. Mengatakan "500 sats" lebih mudah difahami daripada "0.000005 BTC".',
  lightningNetworkLabel: "Rangkaian Lightning:",
  lightningNetworkText:
    "Di Rangkaian Lightning, pembayaran didenominasikan dalam satoshi. Ini membolehkan pembayaran mikro sekecil pecahan sen.",
  convertingSatoshisTo: "Menukar Satoshi kepada {name} ({code})",
  conversionExplanation:
    "Untuk menukar satoshi kepada {name}, bahagikan bilangan satoshi dengan 100,000,000 untuk mendapatkan jumlah BTC, kemudian darabkan dengan kadar pertukaran BTC/{code} semasa. Contoh:",
  calculatorExplanation:
    "Kalkulator di atas melakukan pengiraan ini serta-merta — cuma taip nilai dalam medan satoshi atau medan {code} dan penukaran berlaku dalam masa nyata.",
  quickReference: "Rujukan Pantas",
  convertToOtherCurrencies: "Tukar Satoshi ke mata wang lain",
};

const ka: TranslationStrings = {
  convertSatoshisTo: "სატოშის კონვერტაცია",
  instantlyConvert:
    "მყისიერად გადააქციეთ ბიტკოინ სატოშები (sats) {name}-ში და პირიქით",
  satoshisLabel: "სატოში (sats)",
  enterSatoshis: "შეიყვანეთ თანხა სატოშებში",
  enterAmountIn: "შეიყვანეთ თანხა {code}-ში",
  shareLinkLabel: "გააზიარეთ ეს კონვერტაცია:",
  copyLink: "ბმულის კოპირება",
  linkCopied: "დაკოპირდა!",
  whatIsASatoshi: "რა არის სატოში?",
  whatIsASatoshiBody:
    'სატოში (ხშირად შემოკლებით "sat" ან "sats") არის ბიტკოინის უმცირესი ერთეული. დასახელებულია სატოში ნაკამოტოს, ბიტკოინის ფსევდონიმური შემქმნელის პატივსაცემად.',
  howSatoshisWork: "როგორ მუშაობს სატოში ბიტკოინში",
  fixedSupplyLabel: "ფიქსირებული მიწოდება:",
  fixedSupplyText:
    "მხოლოდ 21 მილიონი ბიტკოინი (21,000,000 BTC) იარსებებს. ეს ზღვარი ბიტკოინის პროტოკოლით არის განმტკიცებული.",
  hundredMillionLabel: "100 მილიონი სატოში თითო ბიტკოინზე:",
  hundredMillionText:
    "თითოეული ბიტკოინი იყოფა 100,000,000 (ასი მილიონ) სატოშად.",
  whySatoshisMatterLabel: "რატომ არის სატოში მნიშვნელოვანი:",
  whySatoshisMatterText:
    'ბიტკოინის ფასის ზრდასთან ერთად, ადამიანები სულ უფრო ხშირად ანგარიშობენ სატოშებში. "500 sats" თქმა ბევრად უფრო ინტუიციურია ვიდრე "0.000005 BTC".',
  lightningNetworkLabel: "Lightning ქსელი:",
  lightningNetworkText:
    "Lightning ქსელში გადახდები სატოშებში (ან მილისატოშებში) ხდება. ეს შესაძლებელს ხდის წვრილმან გადახდებს.",
  convertingSatoshisTo: "სატოშის გადაქცევა {name} ({code})-ში",
  conversionExplanation:
    "სატოშის {name}-ში გადასაყვანად, გაყავით სატოშების რაოდენობა 100,000,000-ზე BTC-ის მისაღებად, შემდეგ გაამრავლეთ მიმდინარე BTC/{code} კურსზე. მაგალითად:",
  calculatorExplanation:
    "ზემოთ მოცემული კალკულატორი ამ გამოთვლას მყისიერად ასრულებს — უბრალოდ შეიყვანეთ მნიშვნელობა სატოშის ან {code} ველში.",
  quickReference: "სწრაფი მითითება",
  convertToOtherCurrencies: "სატოშის სხვა ვალუტებში კონვერტაცია",
};

const it: TranslationStrings = {
  convertSatoshisTo: "Converti Satoshi in",
  instantlyConvert:
    "Converti istantaneamente i satoshi Bitcoin (sats) in {name} e viceversa",
  satoshisLabel: "Satoshi (sats)",
  enterSatoshis: "Inserisci l'importo in satoshi",
  enterAmountIn: "Inserisci l'importo in {code}",
  shareLinkLabel: "Condividi questa conversione:",
  copyLink: "Copia link",
  linkCopied: "Link copiato!",
  whatIsASatoshi: "Cos'è un Satoshi?",
  whatIsASatoshiBody:
    'Un satoshi (spesso abbreviato in "sat" o "sats") è la più piccola unità di Bitcoin. Prende il nome da Satoshi Nakamoto, il creatore pseudonimo di Bitcoin, che ha pubblicato il whitepaper di Bitcoin il 31 ottobre 2008. Il termine "satoshi" è stato proposto dalla prima comunità Bitcoin come un modo conveniente per riferirsi a minuscole frazioni di Bitcoin.',
  howSatoshisWork: "Come funzionano i Satoshi in Bitcoin",
  fixedSupplyLabel: "Offerta fissa:",
  fixedSupplyText:
    "Esisteranno solo 21 milioni di Bitcoin (21.000.000 BTC). Questo tetto massimo è imposto dal protocollo Bitcoin e non può essere modificato.",
  hundredMillionLabel: "100 milioni di Sats per Bitcoin:",
  hundredMillionText:
    "Ogni Bitcoin è divisibile in 100.000.000 (cento milioni) di satoshi. Ciò significa che l'offerta totale di satoshi è di 2.100.000.000.000.000 (2,1 bilardi).",
  whySatoshisMatterLabel: "Perché i Satoshi sono importanti:",
  whySatoshisMatterText:
    'Con l\'aumento del prezzo di Bitcoin, le persone effettuano sempre più transazioni in satoshi anziché in Bitcoin interi. È molto più intuitivo dire "500 sats" che "0,000005 BTC".',
  lightningNetworkLabel: "Rete Lightning:",
  lightningNetworkText:
    "Sulla rete Lightning, i pagamenti sono denominati in satoshi (o anche millisatoshi — millesimi di satoshi). Ciò rende possibili micropagamenti piccoli quanto una frazione di centesimo.",
  convertingSatoshisTo: "Convertire Satoshi in {name} ({code})",
  conversionExplanation:
    "Per convertire i satoshi in {name}, dividi il numero di satoshi per 100.000.000 per ottenere l'importo in BTC, poi moltiplica per il tasso di cambio attuale BTC/{code}. Ad esempio:",
  calculatorExplanation:
    "Il calcolatore qui sopra esegue questo calcolo istantaneamente per qualsiasi importo — basta digitare un valore nel campo satoshi o nel campo {code} e la conversione avviene in tempo reale. Vedrai anche l'importo equivalente in Bitcoin.",
  quickReference: "Riferimento rapido",
  convertToOtherCurrencies: "Converti Satoshi in altre valute",
};

const nl: TranslationStrings = {
  convertSatoshisTo: "Satoshis omrekenen naar",
  instantlyConvert:
    "Reken Bitcoin-satoshis (sats) direct om naar {name} en terug",
  satoshisLabel: "Satoshis (sats)",
  enterSatoshis: "Voer het bedrag in satoshis in",
  enterAmountIn: "Voer het bedrag in {code} in",
  shareLinkLabel: "Deel deze omrekening:",
  copyLink: "Link kopiëren",
  linkCopied: "Gekopieerd!",
  whatIsASatoshi: "Wat is een Satoshi?",
  whatIsASatoshiBody:
    'Een satoshi (vaak afgekort als "sat" of "sats") is de kleinste eenheid van Bitcoin. Het is vernoemd naar Satoshi Nakamoto, de pseudonieme maker van Bitcoin, die op 31 oktober 2008 de Bitcoin-whitepaper publiceerde. De term "satoshi" werd voorgesteld door de vroege Bitcoin-gemeenschap als een handige manier om naar kleine fracties van een Bitcoin te verwijzen.',
  howSatoshisWork: "Hoe Satoshis werken in Bitcoin",
  fixedSupplyLabel: "Vast aanbod:",
  fixedSupplyText:
    "Er zullen ooit slechts 21 miljoen Bitcoin (21.000.000 BTC) bestaan. Dit maximum wordt afgedwongen door het Bitcoin-protocol en kan niet worden gewijzigd.",
  hundredMillionLabel: "100 miljoen Sats per Bitcoin:",
  hundredMillionText:
    "Elke Bitcoin is deelbaar in 100.000.000 (honderd miljoen) satoshis. Dit betekent dat het totale aanbod van satoshis 2.100.000.000.000.000 (2,1 biljard) is.",
  whySatoshisMatterLabel: "Waarom Satoshis belangrijk zijn:",
  whySatoshisMatterText:
    'Naarmate de prijs van Bitcoin stijgt, handelen mensen steeds meer in satoshis in plaats van hele Bitcoins. Het is veel intuïtiever om "500 sats" te zeggen dan "0,000005 BTC".',
  lightningNetworkLabel: "Lightning-netwerk:",
  lightningNetworkText:
    "Op het Lightning-netwerk worden betalingen uitgedrukt in satoshis (of zelfs millisatoshis — duizendsten van een satoshi). Dit maakt microbetalingen zo klein als een fractie van een cent mogelijk.",
  convertingSatoshisTo: "Satoshis omrekenen naar {name} ({code})",
  conversionExplanation:
    "Om satoshis naar {name} om te rekenen, deel het aantal satoshis door 100.000.000 om het BTC-bedrag te krijgen, vermenigvuldig vervolgens met de huidige BTC/{code}-wisselkoers. Bijvoorbeeld:",
  calculatorExplanation:
    "De rekenmachine hierboven doet deze berekening direct voor elk bedrag — typ gewoon een waarde in het satoshi-veld of het {code}-veld en de omrekening gebeurt in realtime. Je ziet ook het equivalente Bitcoin-bedrag.",
  quickReference: "Snelle referentie",
  convertToOtherCurrencies: "Satoshis omrekenen naar andere valuta's",
};

/** Map of language code to display name and translations */
export const TRANSLATIONS: Record<
  string,
  { label: string; strings: TranslationStrings }
> = {
  en: { label: "English", strings: en },
  es: { label: "Español", strings: es },
  pt: { label: "Português", strings: pt },
  ja: { label: "日本語", strings: ja },
  zh: { label: "中文", strings: zh },
  ko: { label: "한국어", strings: ko },
  hi: { label: "हिन्दी", strings: hi },
  tr: { label: "Türkçe", strings: tr },
  ru: { label: "Русский", strings: ru },
  ar: { label: "العربية", strings: ar },
  de: { label: "Deutsch", strings: de },
  fr: { label: "Français", strings: fr },
  th: { label: "ไทย", strings: th },
  pl: { label: "Polski", strings: pl },
  uk: { label: "Українська", strings: uk },
  id: { label: "Bahasa Indonesia", strings: id },
  vi: { label: "Tiếng Việt", strings: vi },
  bn: { label: "বাংলা", strings: bn },
  ms: { label: "Bahasa Melayu", strings: ms },
  ka: { label: "ქართული", strings: ka },
  it: { label: "Italiano", strings: it },
  nl: { label: "Nederlands", strings: nl },
};

/**
 * Map currency code → default (non-English) language code.
 * English-speaking currencies are omitted (they default to "en").
 */
export const CURRENCY_DEFAULT_LANG: Record<string, string> = {
  aed: "ar",
  ars: "es",
  bdt: "bn",
  bhd: "ar",
  brl: "pt",
  clp: "es",
  cny: "zh",
  czk: "en", // Czech not included, default en
  dkk: "en", // Danish not included, default en
  eur: "fr",
  gel: "ka",
  huf: "en", // Hungarian not included, default en
  idr: "id",
  ils: "en", // Hebrew not included, default en
  inr: "hi",
  jpy: "ja",
  krw: "ko",
  kwd: "ar",
  lkr: "en", // Sinhala not included, default en
  mmk: "en", // Burmese not included, default en
  mxn: "es",
  myr: "ms",
  ngn: "en", // English is official in Nigeria
  nok: "en", // Norwegian not included, default en
  php: "en", // English is widely used in Philippines
  pkr: "en", // Urdu not included, default en
  pln: "pl",
  rub: "ru",
  sar: "ar",
  sek: "en", // Swedish not included, default en
  thb: "th",
  try: "tr",
  twd: "zh",
  uah: "uk",
  vef: "es",
  vnd: "vi",
  zar: "en", // English is official in South Africa
};

/** Get the default language for a currency. English currencies return "en". */
export function getDefaultLang(currencyCode: string): string {
  return CURRENCY_DEFAULT_LANG[currencyCode] ?? "en";
}

/** Simple template: replace {name} and {code} placeholders */
export function t(
  template: string,
  vars: { name?: string; code?: string },
): string {
  let s = template;
  if (vars.name) s = s.replace(/\{name\}/g, vars.name);
  if (vars.code) s = s.replace(/\{code\}/g, vars.code);
  return s;
}

/** Map locale code → URL-friendly slug for language permalink */
export const LANG_SLUGS: Record<string, string> = {
  fr: "francais",
  de: "deutsch",
  es: "espanol",
  pt: "portugues",
  it: "italiano",
  nl: "nederlands",
  ja: "nihongo",
  zh: "zhongwen",
  ko: "hangugeo",
  hi: "hindi",
  tr: "turkce",
  ru: "russkiy",
  ar: "arabi",
  th: "thai",
  pl: "polski",
  uk: "ukrainska",
  id: "bahasa-indonesia",
  vi: "tieng-viet",
  bn: "bangla",
  ms: "bahasa-melayu",
  ka: "kartuli",
};

/** Reverse lookup: slug → locale code */
export const SLUG_TO_LANG: Record<string, string> = Object.fromEntries(
  Object.entries(LANG_SLUGS).map(([k, v]) => [v, k]),
);

/**
 * Which languages are available for each currency (excluding English, which is always the base).
 * Currencies not listed get their single CURRENCY_DEFAULT_LANG.
 * EUR is the special case with all major Eurozone languages.
 */
export const CURRENCY_LANGUAGES: Record<string, string[]> = {
  eur: ["fr", "de", "es", "pt", "it", "nl"],
  cad: ["fr"],
};

/** Get the list of available non-English languages for a currency */
export function getAvailableLanguages(currencyCode: string): string[] {
  if (CURRENCY_LANGUAGES[currencyCode]) {
    return CURRENCY_LANGUAGES[currencyCode];
  }
  const defaultLang = getDefaultLang(currencyCode);
  return defaultLang !== "en" ? [defaultLang] : [];
}
